<template>
    <div class="lookup-dropdown" @keydown.esc="isOpen = false">
        <label v-if="label" class="lookup-dropdown__label">{{ label }}</label>

        <div class="lookup-dropdown__search-row">
            <input
                ref="inputRef"
                type="text"
                class="lookup-dropdown__input"
                :class="{ 'lookup-dropdown__input--error': firstError }"
                :placeholder="placeholder"
                :value="searchTerm"
                @input="onInput"
                @focus="onFocus"
                @blur="onBlur"
                @keydown.enter.prevent="onEnterKey"
            />
        </div>

        <div v-if="isOpen" class="lookup-dropdown__menu">
            <div v-if="isLoading" class="lookup-dropdown__state">Se încarcă...</div>
            <template v-else>
                <button
                    v-for="option in options"
                    :key="option.id"
                    type="button"
                    class="lookup-dropdown__option"
                    @mousedown.prevent="selectOption(option)"
                >
                    {{ option.name }}
                </button>
                <button
                    v-if="canAddNew"
                    type="button"
                    class="lookup-dropdown__option lookup-dropdown__option--new"
                    @mousedown.prevent="addOption"
                >
                    + Adaugă „{{ trimmedSearchTerm }}"
                </button>
                <div
                    v-if="options.length === 0 && !canAddNew"
                    class="lookup-dropdown__state"
                >
                    Nu există rezultate.
                </div>
            </template>
        </div>

        <div v-if="errorMessage || firstError" class="lookup-dropdown__error">{{ errorMessage || firstError }}</div>
    </div>
</template>

<script>
export default {
    name: 'SearchLookupDropdown',
    props: {
        modelValue: {
            type: [Number, String, null],
            default: null,
        },
        endpoint: {
            type: String,
            required: true,
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Caută...',
        },
        minimumSearchLength: {
            type: Number,
            default: 1,
        },
        initialName: {
            type: String,
            default: '',
        },
        errors: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'selected'],
    data() {
        return {
            searchTerm: '',
            options: [],
            isOpen: false,
            isLoading: false,
            errorMessage: '',
            debounceTimer: null,
        };
    },
    computed: {
        trimmedSearchTerm() {
            return this.searchTerm.trim();
        },
        canAddNew() {
            if (this.isLoading || this.trimmedSearchTerm.length === 0) return false;
            const alreadyExists = this.options.some(
                (opt) => opt.name.toLowerCase() === this.trimmedSearchTerm.toLowerCase()
            );
            return !alreadyExists;
        },
        firstError() {
            return this.errors && this.errors.length > 0 ? this.errors[0] : null;
        },
    },
    mounted() {
        if (this.initialName) {
            this.searchTerm = this.initialName;
        }
        this.fetchOptions('');
    },
    beforeUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    },
    methods: {
        async fetchOptions(query) {
            if (query.length > 0 && query.length < this.minimumSearchLength) {
                this.options = [];
                return;
            }

            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await window.axios.get(this.endpoint, {
                    params: { q: query },
                });

                this.options = Array.isArray(response.data?.data) ? response.data.data : [];
            } catch {
                this.errorMessage = 'Nu am putut încărca datele.';
                this.options = [];
            } finally {
                this.isLoading = false;
            }
        },
        onInput(event) {
            this.searchTerm = event.target.value;
            this.isOpen = true;

            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.fetchOptions(this.trimmedSearchTerm);
            }, 250);
        },
        onFocus() {
            this.isOpen = true;

            if (this.options.length === 0) {
                this.fetchOptions(this.trimmedSearchTerm);
            }
        },
        onBlur() {
            setTimeout(() => {
                this.isOpen = false;
            }, 120);
        },
        selectOption(option) {
            this.searchTerm = option.name;
            this.isOpen = false;
            this.errorMessage = '';
            this.$emit('update:modelValue', option.id);
            this.$emit('selected', option);
        },
        async addOption() {
            if (!this.canAddNew) return;

            this.isLoading = true;
            this.errorMessage = '';

            try {
                const response = await window.axios.post(this.endpoint, {
                    name: this.trimmedSearchTerm,
                });

                const option = response.data?.data;

                if (!option || typeof option.id === 'undefined') {
                    throw new Error('Invalid lookup response');
                }

                const existingIndex = this.options.findIndex(
                    (entry) => String(entry.id) === String(option.id)
                );

                if (existingIndex === -1) {
                    this.options = [option, ...this.options];
                }

                this.selectOption(option);
            } catch {
                this.errorMessage = 'Nu am putut adăuga valoarea.';
            } finally {
                this.isLoading = false;
            }
        },
        onEnterKey() {
            if (this.options.length > 0) {
                this.selectOption(this.options[0]);
                return;
            }
            if (this.canAddNew) {
                this.addOption();
            }
        },
    },
};
</script>
